
class DepartmentMenu {

    constructor(element) {
        this.element = element;
        this.initialize();
    }

    // Toggles the mobile menu (small viewports only).
    // Uses an .open class on the <ul> because the toggle button and the
    // list are siblings and cannot be connected via CSS alone.
    toggleMenu = (event) => {
        event.stopPropagation();
        const submenu = this.element.querySelector('.department-menu__inner > ul');
        if (!submenu) {
            return;
        }

        const expanded = event.currentTarget.getAttribute('aria-expanded') === 'true';
        event.currentTarget.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        submenu.classList.toggle('open', !expanded);
    }

    async initialize() {
        if (!this.element || this.element.classList.contains('init')) {
            return;
        }
        this.element.classList.add('init');

        const mobileToggle = this.element.querySelector('.department-menu__toggle-menu');
        if (mobileToggle) {
            mobileToggle.addEventListener('click', this.toggleMenu);
        }

        // Single click handler for every disclosure button in the tree.
        for (const button of this.allDisclosureButtons) {
            button.addEventListener('click', this.toggleDisclosure);
        }

        // Close a top-level submenu when focus leaves its <li> entirely
        // (e.g. Tab past the last item inside it).
        for (const topLevelItem of this.element.querySelectorAll('.department-menu__menu > li')) {
            topLevelItem.addEventListener('focusout', (event) => {
                if (!topLevelItem.contains(event.relatedTarget)) {
                    const button = topLevelItem.querySelector(':scope > button[aria-controls]');
                    if (button) this.closeSubMenu(button);
                }
            });
        }

        // Close all open submenus when clicking outside the menu.
        document.addEventListener('click', (event) => {
            if (!this.element.contains(event.target)) {
                this.closeAllSubMenus();
            }
        });

        // On Escape: close the innermost submenu that contains the focused
        // element and return focus to its controlling button.
        this.element.addEventListener('keydown', (event) => {
            if (event.key !== 'Escape') return;

            const openSubmenu = document.activeElement.closest('[id^="submenu-"]');
            if (openSubmenu) {
                const controlButton = this.element.querySelector(`[aria-controls="${openSubmenu.id}"]`);
                if (controlButton) {
                    this.closeSubMenu(controlButton);
                    controlButton.focus();
                    return;
                }
            }
            this.closeAllSubMenus();
        });
    }

    // All buttons that control a submenu (excludes the mobile .department-menu__toggle-menu).
    get allDisclosureButtons() {
        return this.element.querySelectorAll('button[aria-controls]');
    }

    // aria-expanded on the button is the single source of truth for open/closed state.
    isSubMenuOpen(button) {
        return button.getAttribute('aria-expanded') === 'true';
    }

    openSubMenu(button) {
        // Close any sibling submenus at the same level so only one is open at a time.
        const parentList = button.closest('li')?.parentElement;
        if (parentList) {
            for (const sibling of parentList.querySelectorAll(':scope > li > button[aria-controls]')) {
                if (sibling !== button) this.closeSubMenu(sibling);
            }
        }
        button.setAttribute('aria-expanded', 'true');
    }

    closeSubMenu(button) {
        button.setAttribute('aria-expanded', 'false');

        // Recursively collapse any open buttons nested inside this submenu.
        const submenu = document.getElementById(button.getAttribute('aria-controls'));
        if (submenu) {
            for (const childButton of submenu.querySelectorAll('button[aria-controls]')) {
                childButton.setAttribute('aria-expanded', 'false');
            }
        }
    }

    closeAllSubMenus() {
        for (const button of this.element.querySelectorAll('.department-menu__menu > li > button[aria-controls]')) {
            this.closeSubMenu(button);
        }
    }

    // Unified toggle handler for all disclosure buttons at every level.
    toggleDisclosure = (event) => {
        const button = event.currentTarget;
        if (this.isSubMenuOpen(button)) {
            this.closeSubMenu(button);
        } else {
            this.openSubMenu(button);
        }
    }
}

(function (Drupal) {
    Drupal.behaviors.departmentMenu = {
        attach: function (context, settings) {
            once('department-menu', '.department-menu', context).forEach((element) => {
                new DepartmentMenu(element);
            });
        }
    }
})(Drupal);
